import React, { useEffect, useState } from "react";
import {
  Layout,
  Input,
  Button,
  Select,
  Form,
  Card,
  Pagination,
  Row,
  Col,
  Dropdown,
  Menu,
  Modal,
  Spin,
  message,
} from "antd";
import {
  EllipsisOutlined,
  PlusOutlined,
  DeleteOutlined,
  FolderOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import "./index.css";
import surveyGeneratorHeader from "../../assets/img/Banner2.png";
import axios from "axios";
import { toast } from "react-toastify";
import { get, post, remove } from "../../utils/apiUtils";
import WorkspaceModal from "./WorkspaceModal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserRole } from "../../hooks/getUserRoles";
import { normalizeRole, atLeast } from "../../utils/roles";
import GeneralButton from "../../components/GeneralButton/index";
import { sortByCreatedAt } from "../../utils/sorByCreateAt";

const { Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;
const { Search } = Input;

interface Workspace {
  id: number;
  name: string;
  description: string;
  created_at?: string;
}

interface ApiResponse {
  type: string;
  data: Workspace[];
}

const Workspaces: React.FC = () => {
  const { t } = useTranslation("workspaces");
  const navigate = useNavigate();
  const userRole = getUserRole();
  const role = normalizeRole(userRole);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null
  );
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [showProjects, setShowProjects] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await get<any>("countries/");
        console.log("Countries response:", response);
        if (response.type === "success") {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch countries:", error);
        toast.error(t("loadCountriesFailed"));
      }
    };
    fetchCountries();
    localStorage.removeItem("currentProjectId");
    localStorage.removeItem("currentWorkspaceId");
  }, []);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);

  const openModalForEdit = (projectId: any) => {
    setModalMode("edit");
    setCurrentWorkspaceId(projectId);
    setIsModalVisible(true);
  };

  const showModalForCreate = () => {
    setModalMode("create");
    setCurrentWorkspaceId(null);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const fetchData = async (searchTerm = "", countryId?: string) => {
    try {
      const queryParams = new URLSearchParams({
        skip: "0",
        limit: "100",
      });

      if (searchTerm) {
        queryParams.append("name", searchTerm);
      }
      if (countryId) {
        queryParams.append("country_id", countryId);
      }

      const response = await get<any>(
        `organization-workspaces/?${queryParams.toString()}`,
        {}
      );

      if (response.type === "success") {
        console.log("Response from server:", response.data);
        const sortedData = response.data.sort(
          (a: Workspace, b: Workspace) =>
            new Date(b.created_at!).getTime() -
            new Date(a.created_at!).getTime()
        );

        setWorkspaces(sortedData);
      } else {
        toast.error(t("loadWorkspacesFailed"));
      }
    } catch (error) {
      toast.error(t("loadWorkspacesFailed"));
    }
  };

  // 🔁 Search & Country filter – tani bëhet auto sa herë shkruan ose ndërron country
  useEffect(() => {
    fetchData(search, selectedCountry || undefined);
  }, [search, selectedCountry]);

  const handleWorkspaceClick = (workspaceId: any) => {
    navigate(`/projects/${workspaceId}`);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const indexOfLastWorkspace = currentPage * pageSize;
  const indexOfFirstWorkspace = indexOfLastWorkspace - pageSize;
  const currentWorkspaces = workspaces.slice(
    indexOfFirstWorkspace,
    indexOfLastWorkspace
  );

  const handleDelete = async (id: number) => {
    const response = await remove<any>(`workspaces/${id}`);

    if (response.type === "success") {
      const filteredWorkspaces = workspaces.filter(
        (workspace) => workspace.id !== id
      );
      setWorkspaces(filteredWorkspaces);
      message.success(t("deleteSuccess"));
    } else {
      console.error("Failed to delete workspace:", response.data);
      message.error(t("deleteError"));
    }
  };

  const menu = (workspace: Workspace) => (
    <Menu>
      {role !== "viewer" && (
        <>
          <Menu.Item
            key="edit"
            onClick={(e) => {
              e.domEvent.stopPropagation();
              openModalForEdit(workspace.id);
            }}
          >
            {t("edit")}
          </Menu.Item>
          <Menu.Item
            key="delete"
            onClick={(e) => {
              e.domEvent.stopPropagation();
              handleDelete(workspace.id);
            }}
          >
            {t("delete")}
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  let organization_id = localStorage.getItem("organizationId");
  const handleOk = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        const url = `workspaces/?organization_id=${organization_id}&country_id=${values.country_id}`;
        axios
          .post(url, {
            name: values.name,
            description: values.description,
          })
          .then((response) => {
            message.success(t("addSuccess"));
            setWorkspaces(sortByCreatedAt(response.data));

            form.resetFields();
            setIsModalVisible(false);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Failed to add workspace:", error);
            toast.error(t("addError"));
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleCreate = (newWorkspace: Workspace) => {
    setVisible(false);
    setWorkspaces((prev) => {
      const exists = prev.some((w) => w.id === newWorkspace.id);

      if (exists) {
        return prev.map((w) => (w.id === newWorkspace.id ? newWorkspace : w));
      }

      return [newWorkspace, ...prev].sort((a, b) => b.id - a.id);
    });
  };

  const clearAllFilters = () => {
    setSelectedCountry(null);
    setSearch("");
    form.resetFields();
    fetchData();
  };

  return (
    <>
      <div className="workspace-layout" style={{ marginLeft: 0 }}>
        <Content className="content-style">
          <div className="header-image">
            <img
              src={surveyGeneratorHeader}
              alt="workspaces"
              style={{ width: "100%" }}
            />
          </div>

          <Form className="survey-form-workspaces" layout="vertical">
            <div className="card">
              <div className="title-button">
                <h2>{t("title")}</h2>
                <div className="reset-filters-button">
                  {atLeast(role, "manager") && (
                    <GeneralButton
                      className="button-style-new-worskpace"
                      onClick={showModalForCreate}
                      variant="default"
                    >
                      <PlusOutlined /> {t("newWorkspace")}
                    </GeneralButton>
                  )}
                </div>
              </div>

              <div className="searchCountry-section">
                <div className="search-part">
                  <span> {t("search")}</span>
                  <Search
                    placeholder={t("searchPlaceholder")}
                    onSearch={(value) => fetchData(value, selectedCountry)}
                    style={{ width: "100%" }}
                    enterButton
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="country-part">
                  <span> {t("country")}</span>

                  <Select
                    showSearch
                    value={selectedCountry}
                    placeholder={t("countryPlaceholder")}
                    style={{ width: "100%" }}
                    optionFilterProp="children"
                    filterOption={(input: any, option: any) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      setSelectedCountry(value);
                      fetchData(search, value);
                    }}
                    allowClear
                  >
                    {countries.map((country: any) => (
                      <Option key={country.id} value={country.id}>
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                </div>
                <Button
                  className="clear-all-buttonStyle"
                  onClick={clearAllFilters}
                >
                  {t("clearAll")}
                </Button>
              </div>

              <p>
                {t("description")}
              </p>
              <Row gutter={[8, 8]} justify="center" className="workspaces-card">
                {currentWorkspaces.map((workspace) => (
                  <Col
                    key={workspace.id}
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    style={{ maxWidth: "260px" }}
                  >
                    <div
                      className="workspace-panel"
                      onClick={() => handleWorkspaceClick(workspace.id)}
                    >
                      <div className="workspace-panel-back" />
                      <div className="workspace-panel-content">
                        <div className="workspace-panel-icon">
                          <FolderOutlined />
                        </div>
                        {atLeast(role, "manager") && (
                          <Dropdown
                            overlay={menu(workspace)}
                            trigger={["click"]}
                          >
                            <a
                              className="workspace-panel-menu"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <EllipsisOutlined
                                rotate={90}
                                style={{ fontSize: "16px" }}
                              />
                            </a>
                          </Dropdown>
                        )}
                        <div className="workspace-panel-name">
                          {workspace.name}
                        </div>
                        <div className="workspace-panel-open">
                          <ArrowRightOutlined />
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              <Pagination
                style={{ display: "flex", justifyContent: "flex-end" }}
                current={currentPage}
                onChange={(page) => setCurrentPage(page)}
                total={workspaces.length}
                pageSize={pageSize}
                showSizeChanger={false}
              />
            </div>
          </Form>
        </Content>

        <WorkspaceModal
          visible={isModalVisible}
          onCancel={handleModalClose}
          onCreate={handleCreate}
          workspaceId={currentWorkspaceId}
          mode={modalMode}
        />
      </div>
    </>
  );
};

export default Workspaces;
